import {
  Box,
  VStack,
  HStack,
  Heading,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Tag,
  Icon,
  Divider,
  Badge,
  Card,
  CardBody,
  Collapse,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  RadioGroup, // Added
  Radio,      // Added
  Tooltip,    // Added
  ButtonGroup // Added
} from '@chakra-ui/react';
import { useCallback, useState, useEffect } from 'react';
import { MagicWand, ListPlus, User, CaretDown, CaretUp, MagnifyingGlass, Info as InfoIcon } from '@phosphor-icons/react'; // Added InfoIcon
import {
  countries,
  featureCategories
} from '../data/filterOptions';
import { useFeaturesWithMetadata } from '../hooks/useFeatures';
import { getFilterAggregations } from '../utils/elasticsearchApi';
import { getFeatureLabel, getFeatureById } from '../constants/featureRegistry';

export default function SearchFilters({ 
  filters, 
  onFilterChange, 
  onReset, 
  variant = 'default',
  showEra = true,
  showVerification = true,
  showCountry = true,
  features,
  onlyShowFeatures = false,
  filterOptions, // New prop that comes from Elasticsearch aggregations
  onInfoClick, // Prop to handle info icon click
  enableElasticsearchAggregations = false // New prop to control ES fetch
}) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const systemBadgeBg = useColorModeValue('blue.100', 'blue.800');
  const autoBadgeBg = useColorModeValue('purple.100', 'purple.800');
  const customBadgeBg = useColorModeValue('green.100', 'green.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const selectedHoverBg = useColorModeValue('blue.100', 'blue.800');

  const { features: defaultFeaturesObj, loading: featuresLoading, error: featuresError } = useFeaturesWithMetadata();
  const allFeaturesObj = features || defaultFeaturesObj;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usedFeatures, setUsedFeatures] = useState({ system: [], auto: [], manual: [] });
  const [availableCountries, setAvailableCountries] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [featureSearch, setFeatureSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  // Update expanded state when search changes to show matching features
  useEffect(() => {
    if (featureSearch) {
      const allFeatures = [...usedFeatures.system, ...usedFeatures.auto, ...usedFeatures.manual];
      const matchingCategories = new Set();
      
      // Find all categories that have features matching the search
      allFeatures.forEach(feature => {
        if (feature.label.toLowerCase().includes(featureSearch.toLowerCase())) {
          if (feature.category) {
            matchingCategories.add(feature.category.toLowerCase());
          } else {
            matchingCategories.add('uncategorized');
          }
        }
      });
      
      // Auto-expand only categories that have matching features
      setExpandedCategories(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(key => {
          newState[key] = matchingCategories.has(key);
        });
        return newState;
      });
    } else if (featureSearch === '' && Object.values(expandedCategories).every(v => v)) {
      // If search was cleared and all categories were expanded due to search, collapse all
      setExpandedCategories(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(key => {
          newState[key] = false;
        });
        return newState;
      });
    }
  }, [featureSearch, usedFeatures]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        setError(null);

        let aggregationsData = filterOptions;
        
        // If filterOptions are not provided AND enableElasticsearchAggregations is true,
        // get them from Elasticsearch
        if (!aggregationsData && enableElasticsearchAggregations) {
          const result = await getFilterAggregations();
          if (result.success) {
            aggregationsData = result.data;
          } else {
            throw new Error('Failed to load filter options from Elasticsearch');
          }
        }

        // Ensure aggregationsData is an object to prevent errors below,
        // especially when not fetching from Elasticsearch.
        if (!aggregationsData) {
          aggregationsData = {}; // Initialize to empty object
        }

        // Extract countries from aggregations
        const countries = aggregationsData.countries || [];
        const sortedCountries = countries
          .map(country => country.label || country.id)
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        setAvailableCountries(sortedCountries);

        // Extract features from aggregations and combine with existing features
        const elasticsearchFeatures = aggregationsData.features || [];
        
        const systemFeatures = new Map();
        const autoFeatures = new Map();
        const manualFeatures = new Map();

        // Process features from Elasticsearch aggregations - use standardized IDs
        elasticsearchFeatures.forEach(feature => {
          const featureId = feature.id || feature.label;
          const displayLabel = getFeatureLabel(featureId) || feature.label || featureId;
          const elasticsearchFeature = {
            id: featureId, // Use the standardized ID from Elasticsearch
            label: displayLabel, // Use proper display label
            source: 'indexed', // Mark as coming from Elasticsearch
            category: feature.category,
            count: feature.count || 0
          };

          // Use standardized ID as key
          manualFeatures.set(featureId, elasticsearchFeature);
        });

        // Process features from featuresMetadata if available
        if (filters?.featuresMetadata) {
          Object.values(filters.featuresMetadata).forEach(feature => {
            // Don't normalize - use original ID for exact matching
            const featureData = {
              id: feature.id, // Keep original ID
              label: feature.label,
              source: feature.source || 'manual',
              category: feature.category
            };

            if (featureData.source === 'system') {
              systemFeatures.set(feature.id, featureData);
            } else if (featureData.source === 'auto') {
              autoFeatures.set(feature.id, featureData);
            } else {
              manualFeatures.set(feature.id, featureData);
            }
          });
        }

        // Add features from allFeaturesObj
        Object.values(allFeaturesObj || {}).forEach(feature => {
          // Don't normalize - use original ID for exact matching
          const featureData = {
            id: feature.id, // Keep original ID
            label: feature.label,
            source: feature.source || 'manual',
            category: feature.category
          };

          if (featureData.source === 'system') {
            systemFeatures.set(feature.id, featureData);
          } else if (featureData.source === 'auto') {
            autoFeatures.set(feature.id, featureData);
          } else {
            manualFeatures.set(feature.id, featureData);
          }
        });

        const sortByLabel = (a, b) => 
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

        setUsedFeatures({
          system: Array.from(systemFeatures.values()).sort(sortByLabel),
          auto: Array.from(autoFeatures.values()).sort(sortByLabel),
          manual: Array.from(manualFeatures.values()).sort(sortByLabel)
        });

        const initialExpandedState = {
          ...Object.keys(featureCategories).reduce((acc, key) => {
            acc[key] = false;
            return acc;
          }, {}),
          uncategorized: false
        };
        setExpandedCategories(initialExpandedState);

      } catch (err) {
        console.error('Error loading filter options:', err);
        setError('Failed to load filters');
      } finally {
        setLoading(false);
      }
    };

    if (!featuresLoading) {
      loadFilterOptions();
    }
  }, [allFeaturesObj, featuresLoading, filters?.featuresMetadata, filterOptions, enableElasticsearchAggregations]);

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset();
    } else {
      onFilterChange({ features: [] });
    }
  }, [onReset, onFilterChange]);

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  // Prevent search input from losing focus when typing
  const handleSearchInputClick = (e) => {
    e.stopPropagation();
  };
  
  // Add a click handler to the menu to prevent it from closing
  const preventClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'system':
        return {
          bg: systemBadgeBg,
          icon: ListPlus,
          text: 'System'
        };
      case 'auto':
        return {
          bg: autoBadgeBg,
          icon: MagicWand,
          text: 'Auto'
        };
      case 'indexed':
        return {
          bg: useColorModeValue('gray.100', 'gray.700'),
          icon: MagnifyingGlass,
          text: 'Indexed'
        };
      default:
        return {
          bg: customBadgeBg,
          icon: User,
          text: 'Custom'
        };
    }
  };

  // Check if a feature is in the selected filters using exact matching
  const isFeatureSelected = (feature) => {
    if (!Array.isArray(filters.features)) return false;
    
    // Use exact matching for all features
    return filters.features.includes(feature.id);
  };

  const FeatureCheckbox = ({ feature }) => {
    const badge = getSourceBadge(feature.source);
    const isSelected = isFeatureSelected(feature);
    
    return (
      <Box
        as="button"
        w="100%"
        p={2}
        borderWidth="1px"
        borderColor={isSelected ? 'blue.500' : borderColor}
        borderRadius="md"
        bg={isSelected ? selectedBg : 'transparent'}
        _hover={{ bg: isSelected ? selectedHoverBg : hoverBg }}
        transition="all 0.2s"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          // Use different removal logic for Elasticsearch vs other features
          if (feature.source === 'indexed') {
            // For Elasticsearch features, use exact matching
            onFilterChange({ 
              features: isSelected
                ? filters.features.filter(id => id !== feature.id)
                : [...(filters.features || []), feature.id]
            });
          } else {
            // For other features, use exact matching too
            onFilterChange({ 
              features: isSelected
                ? filters.features.filter(id => id !== feature.id)
                : [...(filters.features || []), feature.id]
            });
          }
        }}
        textAlign="left"
      >
        <HStack spacing={2} justify="space-between">
          <Checkbox
            value={feature.id}
            isChecked={isSelected}
            colorScheme="blue"
            size="sm"
            pointerEvents="none"
          >
            {feature.label}
            {feature.count && ` (${feature.count})`}
          </Checkbox>
          {variant === 'default' && (
            <Tag size="sm" bg={badge.bg} borderRadius="full">
              <Icon as={badge.icon} mr={1} />
              {badge.text}
            </Tag>
          )}
        </HStack>
      </Box>
    );
  };

  const CategorySection = ({ categoryKey, category }) => {
    // Features are already processed and categorized in the useEffect hook via 'usedFeatures'
    // or directly from 'filters.featuresMetadata' which should have standardized IDs and categories.
    const allAvailableFeatures = filters?.featuresMetadata ? Object.values(filters.featuresMetadata) : [];
    
    const features = allAvailableFeatures.filter(feature => {
      const featureCategory = feature.category?.toLowerCase() || 'uncategorized';
      return featureCategory === categoryKey;
    }).filter(feature => 
      feature.label.toLowerCase().includes(featureSearch.toLowerCase())
    );
    
    if (features.length === 0) return null;

    const isExpanded = expandedCategories[categoryKey];
    const selectedCount = features.filter(feature => isFeatureSelected(feature)).length;

    if (variant === 'flat') {
      return (
        <Box>
          <HStack 
            justify="space-between" 
            mb={2} 
            onClick={() => toggleCategory(categoryKey)}
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
          >
            <Text fontWeight="medium" fontSize="sm">
              {category.label}
              {selectedCount > 0 && ` (${selectedCount})`}
            </Text>
            <IconButton
              icon={isExpanded ? <CaretUp /> : <CaretDown />}
              variant="ghost"
              size="xs"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            />
          </HStack>
          <Collapse in={isExpanded}>
            <SimpleGrid columns={[2, 3]} spacing={2}>
              {features.map(feature => (
                <FeatureCheckbox key={feature.id} feature={feature} />
              ))}
            </SimpleGrid>
          </Collapse>
        </Box>
      );
    }

    return (
      <Card 
        variant="outline" 
        bg={cardBg} 
        mb={4}
        borderColor={selectedCount > 0 ? 'blue.500' : borderColor}
      >
        <CardBody p={0}>
          <Box
            p={4}
            cursor="pointer"
            onClick={() => toggleCategory(categoryKey)}
            _hover={{ bg: hoverBg }}
          >
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">{category.label}</Text>
                <Text fontSize="sm" color="gray.500">
                  {selectedCount > 0 && `${selectedCount} selected • `}{features.length} features
                </Text>
              </VStack>
              <IconButton
                icon={isExpanded ? <CaretUp /> : <CaretDown />}
                variant="ghost"
                size="sm"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              />
            </HStack>
          </Box>
          <Collapse in={isExpanded}>
            <Box p={4} pt={0}>
              <Stack spacing={2}>
                {features.map(feature => (
                  <FeatureCheckbox key={feature.id} feature={feature} />
                ))}
              </Stack>
            </Box>
          </Collapse>
        </CardBody>
      </Card>
    );
  };

  const FeaturesSection = () => (
    <Box gridColumn={onlyShowFeatures ? "span 4" : "span 2"}>
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="medium" fontSize="sm">Features</Text>
        <Button size="xs" variant="ghost" onClick={handleReset}>
          Reset
        </Button>
      </HStack>
        <InputGroup size="sm" mb={2}>
          <InputLeftElement>
            <Icon as={MagnifyingGlass} color="gray.500" />
          </InputLeftElement>
          <Input
            placeholder="Search features..."
            value={featureSearch}
            onChange={(e) => setFeatureSearch(e.target.value)}
            borderRadius="md"
            size="sm"
            onClick={handleSearchInputClick}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus={featureSearch !== ''}
          />
        </InputGroup>
      <Box maxH="200px" overflowY="auto">
        <Stack spacing={4}>
          {loading || featuresLoading ? (
            <Spinner size="sm" />
          ) : error || featuresError ? (
            <Alert status="error" size="sm">
              <AlertIcon />
              {error || featuresError}
            </Alert>
          ) : (
            (() => {
              const categoriesFromMetadata = new Map();
              if (filters?.featuresMetadata) {
                Object.values(filters.featuresMetadata).forEach(feature => {
                  const categoryKey = feature.category?.toLowerCase() || 'uncategorized';
                  if (!categoriesFromMetadata.has(categoryKey)) {
                    categoriesFromMetadata.set(categoryKey, {
                      key: categoryKey,
                      label: featureCategories[categoryKey]?.label || (categoryKey === 'uncategorized' ? 'Other Features' : categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1))
                    });
                  }
                });
              }
              
              // Ensure 'uncategorized' is present if there are any uncategorized features
              const hasUncategorized = Object.values(filters?.featuresMetadata || {}).some(f => !f.category || f.category.toLowerCase() === 'uncategorized');
              if (hasUncategorized && !categoriesFromMetadata.has('uncategorized')) {
                categoriesFromMetadata.set('uncategorized', { key: 'uncategorized', label: 'Other Features' });
              }

              return Array.from(categoriesFromMetadata.values())
                .sort((a, b) => a.label.localeCompare(b.label)) // Sort categories alphabetically
                .map(({ key, label }) => (
                  <CategorySection 
                    key={key}
                    categoryKey={key}
                    category={{ label }} // Pass category object with label
                  />
                ));
            })()
          )}
        </Stack>
      </Box>
    </Box>
  );

  if (variant === 'flat') {
    if (onlyShowFeatures) {
      return (
        <Box py={2}>
          <SimpleGrid columns={1} spacing={4} alignItems="start">
            <FeaturesSection />
          </SimpleGrid>
        </Box>
      );
    }
    return (
      <Box py={2}>
        <SimpleGrid columns={4} spacing={4} alignItems="start">
          {showCountry && (
            <Box>
              <Text fontWeight="medium" mb={2} fontSize="sm">Countries</Text>
              <InputGroup size="sm" mb={2}>
                <InputLeftElement>
                  <Icon as={MagnifyingGlass} color="gray.500" />
                </InputLeftElement>
                <Input
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  borderRadius="md"
                  size="sm"
                  onClick={handleSearchInputClick}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus={countrySearch !== ''}
                />
              </InputGroup>
              <Box maxH="200px" overflowY="auto">
                <CheckboxGroup
                  value={filters.countries}
                  onChange={(values) => onFilterChange({ countries: values })}
                >
                  <Stack spacing={1}>
                    {availableCountries
                      .filter(country => 
                        country.toLowerCase().includes(countrySearch.toLowerCase())
                      )
                      .map((country) => (
                        <Box
                          key={country}
                          as="button"
                          w="100%"
                          p={2}
                          borderWidth="1px"
                          borderColor={filters.countries?.includes(country) ? 'blue.500' : borderColor}
                          borderRadius="md"
                          bg={filters.countries?.includes(country) ? selectedBg : 'transparent'}
                          _hover={{ bg: filters.countries?.includes(country) ? selectedHoverBg : hoverBg }}
                          transition="all 0.2s"
                          onClick={() => {
                            const isSelected = filters.countries?.includes(country);
                            const newCountries = isSelected
                              ? filters.countries.filter(c => c !== country)
                              : [...(filters.countries || []), country];
                            onFilterChange({ countries: newCountries });
                          }}
                          textAlign="left"
                        >
                          <Checkbox
                            value={country}
                            isChecked={filters.countries?.includes(country)}
                            colorScheme="blue"
                            size="sm"
                            pointerEvents="none"
                          >
                            {country}
                          </Checkbox>
                        </Box>
                      ))}
                  </Stack>
                </CheckboxGroup>
              </Box>
            </Box>
          )}

          <FeaturesSection />

          {showEra && (
            <Box>
              <Text fontWeight="medium" mb={2} fontSize="sm">Era Filter</Text>
              <Box>
                <Text fontSize="xs" mb={1}>Era</Text>
                <Select
                  size="sm"
                  value={filters.era}
                  onChange={(e) => onFilterChange({ era: e.target.value })}
                  placeholder="All Eras"
                >
                  <option value="prehistoric">Prehistoric</option>
                  <option value="ancient">Ancient (3000 BCE - 500 CE)</option>
                  <option value="classical">Classical (500 BCE - 500 CE)</option>
                  <option value="medieval">Medieval (500 - 1500 CE)</option>
                  <option value="modern">Modern (1500 CE - Present)</option>
                </Select>
              </Box>
            </Box>
          )}
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <Box 
      borderWidth={1} 
      borderColor="ancient.mapBorder"
      borderRadius="lg" 
      p={4}
      className="map-texture"
      boxShadow="sm"
    >
      <VStack spacing={6} align="stretch">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading size="md" color="ancient.mapText">Filters</Heading>
          <Button 
            size="sm" 
            onClick={handleReset} 
            variant="ghost"
            color="ancient.terracotta"
            _hover={{ bg: 'ancient.lightStone' }}
          >
            Reset
          </Button>
        </Box>

        <Accordion allowMultiple defaultIndex={[]} allowToggle>
          {/* Countries */}
          <AccordionItem border="none">
            <AccordionButton px={0} _hover={{ bg: 'ancient.papyrus' }}>
              <HStack flex="1" justify="space-between">
                <Text fontWeight="medium" color="ancient.mapText">Countries</Text>
                {filters.countries?.length > 0 && (
                  <Badge 
                    bg="ancient.terracotta" 
                    color="white"
                    borderRadius="full"
                  >
                    {filters.countries.length} selected
                  </Badge>
                )}
              </HStack>
              <AccordionIcon color="ancient.terracotta" />
            </AccordionButton>
            <AccordionPanel pb={4} px={0}>
              <InputGroup size="sm" mb={4}>
                <InputLeftElement>
                  <Icon as={MagnifyingGlass} color="ancient.terracotta" />
                </InputLeftElement>
                <Input
                  variant="map"
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  onClick={handleSearchInputClick}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus={countrySearch !== ''}
                />
              </InputGroup>
              <CheckboxGroup
                value={filters.countries}
                onChange={(values) => onFilterChange({ countries: values })}
              >
                <Stack spacing={2}>
                  {availableCountries
                    .filter(country => 
                      country.toLowerCase().includes(countrySearch.toLowerCase())
                    )
                    .map((country) => (
                      <Box
                        key={country}
                        as="button"
                        w="100%"
                        p={2}
                        borderWidth="1px"
                        borderColor={filters.countries?.includes(country) ? 'ancient.terracotta' : 'ancient.mapBorder'}
                        borderRadius="md"
                        bg={filters.countries?.includes(country) ? 'ancient.lightStone' : 'ancient.papyrus'}
                        _hover={{ bg: 'ancient.lightStone' }}
                        transition="all 0.2s"
                        onClick={() => {
                          const isSelected = filters.countries?.includes(country);
                          const newCountries = isSelected
                            ? filters.countries.filter(c => c !== country)
                            : [...(filters.countries || []), country];
                          onFilterChange({ countries: newCountries });
                        }}
                        textAlign="left"
                      >
                        <Checkbox
                          value={country}
                          isChecked={filters.countries?.includes(country)}
                          colorScheme="ancient"
                          color="ancient.mapText"
                          pointerEvents="none"
                        >
                          {country}
                        </Checkbox>
                      </Box>
                    ))}
                </Stack>
              </CheckboxGroup>
            </AccordionPanel>
          </AccordionItem>

          {/* Features */}
          <AccordionItem border="none">
            <AccordionButton px={0} _hover={{ bg: 'ancient.papyrus' }}>
              <HStack flex="1" justify="space-between">
                <Text fontWeight="medium" color="ancient.mapText">Features</Text>
                {filters.features?.length > 0 && (
                  <Badge 
                    bg="ancient.terracotta" 
                    color="white"
                    borderRadius="full"
                  >
                    {filters.features.length} selected
                  </Badge>
                )}
              </HStack>
              <AccordionIcon color="ancient.terracotta" />
            </AccordionButton>
            <AccordionPanel pb={4} px={0}>
              {/* Feature Filter Logic Toggle MOVED HERE */}
              <HStack mb={4} justify="space-between" align="center">
                <Text fontSize="sm" fontWeight="medium" color="ancient.mapText">
                  Match Features:
                </Text>
                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button
                    onClick={() => onFilterChange({ featureFilterLogic: 'AND' })}
                    isActive={filters.featureFilterLogic === 'AND'}
                    bg={filters.featureFilterLogic === 'AND' ? useColorModeValue('ancient.terracotta', 'ancient.gold') : 'transparent'}
                    color={filters.featureFilterLogic === 'AND' ? useColorModeValue('gray.800', 'ancient.ink') : useColorModeValue('ancient.ink', 'ancient.parchment')}
                    borderColor="ancient.mapBorder"
                    _hover={{ 
                      bg: filters.featureFilterLogic === 'AND' 
                        ? useColorModeValue('ancient.terracottaHover', 'ancient.goldHover') 
                        : useColorModeValue('gray.100', 'gray.700')
                    }}
                  >
                    AND
                  </Button>
                  <Button
                    onClick={() => onFilterChange({ featureFilterLogic: 'OR' })}
                    isActive={filters.featureFilterLogic === 'OR'}
                    bg={filters.featureFilterLogic === 'OR' ? useColorModeValue('ancient.terracotta', 'ancient.gold') : 'transparent'}
                    color={filters.featureFilterLogic === 'OR' ? useColorModeValue('gray.800', 'ancient.ink') : useColorModeValue('ancient.ink', 'ancient.parchment')}
                    borderColor="ancient.mapBorder"
                    _hover={{ 
                      bg: filters.featureFilterLogic === 'OR' 
                        ? useColorModeValue('ancient.terracottaHover', 'ancient.goldHover') 
                        : useColorModeValue('gray.100', 'gray.700')
                    }}
                  >
                    OR
                  </Button>
                </ButtonGroup>
                <Tooltip
                  label="Click to learn how AND/OR logic affects feature filtering."
                  placement="top"
                  hasArrow
                  bg="ancient.ink"
                  color="ancient.parchment"
                >
                  <IconButton
                    icon={<Icon as={InfoIcon} />}
                    size="xs"
                    variant="ghost"
                    color="ancient.terracotta"
                    aria-label="Feature filter logic info"
                    onClick={onInfoClick}
                  />
                </Tooltip>
              </HStack>
              <InputGroup size="sm" mb={4}>
                <InputLeftElement>
                  <Icon as={MagnifyingGlass} color="ancient.terracotta" />
                </InputLeftElement>
                <Input
                  variant="map"
                  placeholder="Search features..."
                  value={featureSearch}
                  onChange={(e) => setFeatureSearch(e.target.value)}
                  onClick={handleSearchInputClick}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus={featureSearch !== ''}
                />
              </InputGroup>
              {loading || featuresLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <Spinner size="sm" color="ancient.terracotta" />
                </Box>
              ) : error || featuresError ? (
                <Alert status="error" size="sm" borderRadius="md">
                  <AlertIcon />
                  {error || featuresError}
                </Alert>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {[
                    ...Object.entries(featureCategories).map(([key, category]) => ({
                      key,
                      category,
                      label: category.label
                    })),
                    {
                      key: 'uncategorized',
                      category: { label: 'Other Features' },
                      label: 'Other Features'
                    }
                  ].map(({ key, category }) => (
                    <CategorySection 
                      key={key}
                      categoryKey={key}
                      category={category}
                    />
                  ))}
                </VStack>
              )}
            </AccordionPanel>
          </AccordionItem>

          {/* Other Filters */}
          <AccordionItem border="none">
            <AccordionButton px={0} _hover={{ bg: 'ancient.papyrus' }}>
              <HStack flex="1" justify="space-between">
                <Text fontWeight="medium" color="ancient.mapText">Other Filters</Text>
                {(filters.materialType || filters.era) && (
                  <Badge 
                    bg="ancient.terracotta" 
                    color="white"
                    borderRadius="full"
                  >
                    Active
                  </Badge>
                )}
              </HStack>
              <AccordionIcon color="ancient.terracotta" />
            </AccordionButton>
            <AccordionPanel pb={4} px={0}>
              <VStack spacing={4}>
                {/* Era */}
                <Box w="full">
                  <Text fontSize="sm" mb={2} color="ancient.mapText">Era</Text>
                  <Select
                    variant="map"
                    value={filters.era}
                    onChange={(e) => onFilterChange({ era: e.target.value })}
                    placeholder="All Eras"
                  >
                    <option value="prehistoric">Prehistoric</option>
                    <option value="ancient">Ancient (3000 BCE - 500 CE)</option>
                    <option value="classical">Classical (500 BCE - 500 CE)</option>
                    <option value="medieval">Medieval (500 - 1500 CE)</option>
                    <option value="modern">Modern (1500 CE - Present)</option>
                  </Select>
                </Box>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </Box>
  );
}
